'use client';

import { createStore, StoreSnapshot } from '@xstate/store';

import type {
  DigineticaBrand,
  DigineticaCategory,
  DigineticaContent,
  DigineticaSuggestion,
  DigineticaTap
} from '@/fo/api/diginetica';
import type { MappedProduct } from '@/fo/util/map-product-data';

export interface ContextStoreSearchSuggestion {
  query: string;
  history: string[];
  products: MappedProduct[];
  correction: string;
  redirect: string;
  sts: DigineticaSuggestion[];
  taps: DigineticaTap[];
  categories: DigineticaCategory[];
  brands: DigineticaBrand[];
  contents: DigineticaContent[];
  isLoading: boolean;
  suggestionLimit: number;
}

export const DEFAULT_SUGGESTION_LIMIT = 5;

export const initialContext: ContextStoreSearchSuggestion = {
  query: '',
  history: [],
  products: [],
  correction: '',
  redirect: '',
  sts: [],
  taps: [],
  categories: [],
  brands: [],
  contents: [],
  isLoading: false,
  suggestionLimit: DEFAULT_SUGGESTION_LIMIT
};

export const storeSearchSuggestion = createStore({
  context: initialContext,
  on: {
    set: (
      context: ContextStoreSearchSuggestion,
      event: {
        state: Partial<ContextStoreSearchSuggestion>;
      }
    ) => {
      return {
        ...context,
        ...event.state
      };
    },
    setQuery: (
      context: ContextStoreSearchSuggestion,
      event: {
        query: string;
      }
    ) => {
      return {
        ...context,
        query: event.query
      };
    },
    addToHistory: (
      context: ContextStoreSearchSuggestion,
      event: {
        query: string;
      }
    ) => {
      return {
        ...context,
        history: [
          event.query,
          ...context.history.filter((q) => q !== event.query)
        ]
      };
    },
    removeFromHistory: (
      context: ContextStoreSearchSuggestion,
      event: {
        query: string;
      }
    ) => {
      return {
        ...context,
        history: context.history.filter((q) => q !== event.query)
      };
    },
    clearHistory: (context: ContextStoreSearchSuggestion) => {
      return {
        ...context,
        history: []
      };
    },
    setLoading: (
      context: ContextStoreSearchSuggestion,
      event: {
        isLoading: boolean;
      }
    ) => {
      return {
        ...context,
        isLoading: event.isLoading
      };
    }
  }
});

export const selectQuery = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.query;
export const selectHistory = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.history;
export const selectProducts = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.products;
export const selectSuggestions = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.sts;
export const selectCategories = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.categories;
export const selectArticles = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.contents;
export const selectIsLoading = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.isLoading;
export const selectSuggestionLimit = (
  state: StoreSnapshot<ContextStoreSearchSuggestion>
) => state.context.suggestionLimit;

// storeSearchSuggestion.inspect(console.debug);
